"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Subject_1 = require("rxjs/Subject");
const ReplaySubject_1 = require("rxjs/ReplaySubject");
const fromEvent_1 = require("rxjs/observable/fromEvent");
exports.attributeToBoolean = (value) => value !== undefined && value !== null && value !== false && value !== 'false';
function ViewChild(selector) {
    return (target, propertyKey) => {
        Object.defineProperty(target.constructor.prototype, propertyKey, {
            get: function () {
                return this['querySelector'](selector);
            },
        });
    };
}
exports.ViewChild = ViewChild;
function ViewChildEvent(selector, event) {
    return (target, propertyKey) => {
        const oldCallback = target.constructor.prototype.connectedCallback || (() => { });
        target.constructor.prototype.connectedCallback = function (arg) {
            const oldReturnValue = oldCallback.call(this, arg);
            createReactiveProperty({
                target: target.constructor.prototype,
                propertyKey,
                createObservable: () => fromEvent_1.fromEvent(this['querySelector'](selector), event),
            });
            return oldReturnValue;
        };
    };
}
exports.ViewChildEvent = ViewChildEvent;
function ViewChildren(selector) {
    return (target, propertyKey) => {
        Object.defineProperty(target.constructor.prototype, propertyKey, {
            get: function () {
                return Array.from(this['querySelectorAll'](selector));
            },
        });
    };
}
exports.ViewChildren = ViewChildren;
function Callback(callbackName) {
    return (target, propertyKey) => {
        const { subject } = createReactiveProperty({
            target: target.constructor.prototype,
            propertyKey,
            createObservable: () => new Subject_1.Subject(),
        });
        const oldCallback = target.constructor.prototype[callbackName] || (() => { });
        target.constructor.prototype[callbackName] = function (arg) {
            if (this[propertyKey]) {
                this[subject].next(arg);
            }
            return oldCallback.call(this, arg);
        };
    };
}
exports.Callback = Callback;
function ReplayCallback(callbackName) {
    return (target, propertyKey) => {
        const { subject } = createReactiveProperty({
            target: target.constructor.prototype,
            propertyKey,
            createObservable: () => new ReplaySubject_1.ReplaySubject(),
        });
        const oldCallback = target.constructor.prototype[callbackName] || (() => { });
        target.constructor.prototype[callbackName] = function (arg) {
            if (this[propertyKey]) {
                this[subject].next(arg);
            }
            return oldCallback.call(this, arg);
        };
    };
}
exports.ReplayCallback = ReplayCallback;
function Attribute(attrName, parser) {
    return (target, propertyKey) => {
        const oldAttributeChangedCallback = target.constructor.prototype['attributeChangedCallback'] || (() => { });
        addObservedAttributes(target, attrName);
        target.constructor.prototype['attributeChangedCallback'] = function (attr, oldValue, newValue) {
            if (attr === attrName) {
                try {
                    this[propertyKey] = parser(newValue);
                }
                catch (error) {
                    console.warn(`${target.constructor.name}[${attrName}->${propertyKey}] json parse failed`, newValue);
                }
            }
            return oldAttributeChangedCallback.call(this, attr, oldValue, newValue);
        };
    };
}
exports.Attribute = Attribute;
exports.StringAttribute = (attrName) => Attribute(attrName, s => s);
exports.JsonAttribute = (attrName) => Attribute(attrName, JSON.parse);
exports.BooleanAttribute = (attrName) => Attribute(attrName, exports.attributeToBoolean);
function ReactiveAttribute(attrName, setterName, parser = a => a) {
    return (target, propertyKey) => {
        const oldAttributeChangedCallback = target.constructor.prototype['attributeChangedCallback'] || (() => { });
        addObservedAttributes(target, attrName);
        const { subject } = createReactiveProperty({
            target: target.constructor.prototype,
            propertyKey,
            createObservable: () => new ReplaySubject_1.ReplaySubject(1),
        });
        if (setterName) {
            Object.defineProperty(target.constructor.prototype, setterName, {
                set: function (value) {
                    this[subject].next(value);
                },
                get: function () {
                    let val;
                    this[subject].subscribe(_val => (val = _val)).unsubscribe();
                    return val;
                },
            });
        }
        target.constructor.prototype['attributeChangedCallback'] = function (attr, oldValue, newValue) {
            if (attr === attrName && this[propertyKey]) {
                try {
                    let parsed = parser(newValue);
                    this[subject].next(parsed);
                }
                catch (error) {
                    console.warn(`${target.constructor.name}[${attrName}->${propertyKey}] parse failed`, newValue);
                }
            }
            return oldAttributeChangedCallback.call(this, attr, oldValue, newValue);
        };
    };
}
exports.ReactiveAttribute = ReactiveAttribute;
function createReactiveProperty({ target, propertyKey, createObservable }) {
    const subject = `__callback_${propertyKey}_subject`;
    const observable = `__callback_${propertyKey}_observable`;
    Object.defineProperty(target.constructor.prototype, subject, {
        get: function () {
            const memoizedSubject = '__memoized' + subject;
            if (!this[memoizedSubject]) {
                this[memoizedSubject] = createObservable();
            }
            return this[memoizedSubject];
        },
    });
    Object.defineProperty(target.constructor.prototype, observable, {
        get: function () {
            const memoizedObservable = '__memoized' + observable;
            if (!this[memoizedObservable]) {
                this[memoizedObservable] = this[subject].asObservable ? this[subject].asObservable() : this[subject];
            }
            return this[memoizedObservable];
        },
    });
    Object.defineProperty(target.constructor.prototype, propertyKey, {
        get: function () {
            return this[observable];
        },
    });
    return { subject, observable };
}
function addObservedAttributes(target, attributeName) {
    const observedAttributes = [attributeName].concat(target.constructor['observedAttributes'] || []);
    Object.defineProperty(target.constructor, 'observedAttributes', {
        configurable: true,
        get() {
            return observedAttributes;
        },
    });
}
//# sourceMappingURL=reactive-decorators.js.map